import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { g, p, beep, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BellRing, Check, Volume2, Repeat, Maximize, Minimize, Timer, Undo2, Pizza, Ban, Play, ChevronDown, ChevronUp, GripVertical, ListRestart, MessageSquare, Hand } from "lucide-react";
import { ParlaReparto } from "@/components/ParlaReparto";
import { annuncia, sbloccaVoce, vocePronta, tieniAcceso, riprendiAcceso } from "@/lib/voce";
import { useOrdineManuale } from "@/lib/ordineManuale";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API_BASE = process.env.REACT_APP_BACKEND_URL;


// Ogni reparto ha il suo colore: le righe degli altri reparti si vedono ma non si toccano
const COL_REPARTO = {
  Pizzeria: "border-orange-300 bg-orange-50 text-orange-900",
  Cucina: "border-emerald-300 bg-emerald-50 text-emerald-900",
  Bar: "border-sky-300 bg-sky-50 text-sky-900",
};
const BADGE_REPARTO = {
  Pizzeria: "bg-orange-600",
  Cucina: "bg-emerald-600",
  Bar: "bg-sky-600",
};

// Righe mie e degli altri reparti messe nello stesso giro, così si capisce cosa esce insieme
const conAltriReparti = (r) => {
  const altri = r.altrui || [];
  const giri = [...new Set([...r.items, ...altri].map((i) => i.giro || 1))].sort((a, b) => a - b);
  return giri.flatMap((gi) => [
    ...r.items.filter((i) => (i.giro || 1) === gi).map((i) => ({ ...i, _altro: false })),
    ...altri.filter((i) => (i.giro || 1) === gi).map((i) => ({ ...i, _altro: true })),
  ]);
};

const spoken = (r, t, completo) => {
  const where = r.mode === "asporto" ? `${t("c.takeaway")} ${r.tables[0]}` : `${t("s.table")} ${r.tables.join(", ")}`;
  const daFare = r.items.filter((i) => !i.ready);
  if (!completo) {
    // Annuncio breve: frasi sempre uguali, così l'audio si riusa in tutti i locali
    const n = daFare.reduce((s, i) => s + (i.qty || 1), 0);
    if (!n) return `${where}. Tutto pronto.`;
    return `${where}. ${n === 1 ? "Un piatto nuovo" : `${n} piatti nuovi`}.`;
  }
  const dishes = daFare.map((i) => `${i.qty} ${i.name}${i.note ? `, ${i.note}` : ""}`).join(". ");
  return `${where}. ${dishes || "tutto pronto"}.`;
};

// Tasto "Pronto" grande: col dito sporco basta appoggiarlo e tenerlo premuto un attimo
const BottonePronto = ({ onDone, label, hint, disabled, testid }) => {
  const timer = useRef(null);
  const [carico, setCarico] = useState(false);
  const stop = () => {
    clearTimeout(timer.current);
    timer.current = null;
    setCarico(false);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <button
      disabled={disabled}
      data-testid={testid}
      className={`relative w-full overflow-hidden rounded-xl bg-slate-900 px-4 py-5 text-center text-lg font-bold uppercase tracking-wide text-white disabled:opacity-40 ${
        carico ? "scale-[0.98]" : ""
      }`}
      onPointerDown={() => {
        if (disabled) return;
        setCarico(true);
        timer.current = setTimeout(() => {
          stop();
          onDone();
        }, 600);
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
    >
      {carico && <span className="absolute inset-y-0 left-0 w-full origin-left animate-[grow_0.6s_linear] bg-emerald-500/40" />}
      <span className="relative flex items-center justify-center gap-2">
        <Check className="h-6 w-6" /> {label}
      </span>
      <span className="relative mt-1 block text-[11px] font-medium normal-case tracking-normal text-white/70">{hint}</span>
    </button>
  );
};

export default function Monitor() {
  const { departments, user, settings } = useApp();
  const { has, askUpgrade } = usePlan();
  const { t } = useI18n();
  const [dept, setDept] = useState(() =>
    departments.includes(user?.department) ? user.department : departments[0]
  );
  const [rows, setRows] = useState([]);
  const [done, setDone] = useState({ dishes: 0, tickets: 0 });
  const [autoVoice, setAutoVoice] = useState(false);
  const [full, setFull] = useState(false);
  const [dough, setDough] = useState(null);
  const [doughVal, setDoughVal] = useState("");
  const [susp, setSusp] = useState(null);
  const [openSoon, setOpenSoon] = useState({});
  const [suspOpen, setSuspOpen] = useState(false);
  const [suspReason, setSuspReason] = useState("");
  const spokenIds = useRef(new Set());
  const seenReq = useRef(new Set());
  const primed = useRef(false);
  const [sbloccato, setSbloccato] = useState(vocePronta());
  const [maniSporche, setManiSporche] = useState(() => localStorage.getItem("rh_mani_sporche") === "1");
  const press = useRef(null);
  const audio = useRef(null);

  const sigRef = useRef("-");
  const [segnali, setSegnali] = useState([]);
  const [parlaPer, setParlaPer] = useState(null);
  const [fatte, setFatte] = useState(null);
  const [dettaglio, setDettaglio] = useState({});

  const prontoTutta = async (r) => {
    try {
      const res = await p(`/kds/${r.order_id}/${dept}/ready-all`);
      toast.success(res.quante === 1 ? t("m.readyOne") : t("m.readyAllDone", { n: res.quante }));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const vistiRef = useRef(new Set());
  const primoRef = useRef(true);
  const fatteRef = useRef(false);
  const [nuovi, setNuovi] = useState([]);
  const segnaliVisti = useRef(new Set());

  // Blocco 3: la sequenza deciso col dito dal reparto (indipendente da giri e ritiri).
  const { righe, dragId, handlers, manuale, azzera } = useOrdineManuale(dept, rows, user?.name);

  const load = useCallback(async () => {
    const [r, d, dg, sp, sg] = await Promise.all([
      g(`/kds/${dept}`, { sig: sigRef.current }), g(`/kds/${dept}/done-today`), g("/dough"), g("/takeaway/suspend"),
      g(`/kds/${dept}/segnali`).catch(() => ({ ricevuti: [] })),
    ]);
    // Polling leggero: "nv" = niente di nuovo, la lista resta com'è.
    if (!r.nv) {
      sigRef.current = r.sig;
      setRows(r.data);
      // Comanda appena entrata: resta segnalata finché il reparto non la guarda davvero
      const arrivi = r.data.filter((x) => !vistiRef.current.has(`${x.order_id}|${x.items.map((i) => i.id).sort().join(",")}`));
      if (arrivi.length) {
        arrivi.forEach((x) => vistiRef.current.add(`${x.order_id}|${x.items.map((i) => i.id).sort().join(",")}`));
        if (!primoRef.current) setNuovi((n) => [...new Set([...n, ...arrivi.map((x) => x.order_id)])]);
      }
      primoRef.current = false;
    }
    setDone(d);
    setDough(dg);
    setSusp(sp);
    const arrivati = sg.ricevuti || [];
    setSegnali(arrivati);
    // Il messaggio di un altro reparto si fa sentire: una campanella e l'avviso a schermo
    arrivati.forEach((s) => {
      if (segnaliVisti.current.has(s.id)) return;
      segnaliVisti.current.add(s.id);
      toast.info(`${s.from_dept}: ${s.testo}${s.table ? ` · tavolo ${s.table}` : ""}`, { duration: 10000 });
      beep(true);
    });
    if (fatteRef.current) setFatte(await g(`/kds/${dept}/fatte`));
  }, [dept]);

  // Cambio reparto: il monitor riparte pulito (niente avvisi o "fatte" dell'altro reparto)
  useEffect(() => {
    vistiRef.current = new Set();
    primoRef.current = true;
    sigRef.current = "-";
    setNuovi([]);
    setFatte(null);
    fatteRef.current = false;
  }, [dept]);

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [load]);

  // Con la voce accesa lo schermo resta acceso: il tablet in cucina non va in stand-by
  useEffect(() => {
    tieniAcceso(autoVoice);
    if (!autoVoice) return;
    document.addEventListener("visibilitychange", riprendiAcceso);
    return () => {
      document.removeEventListener("visibilitychange", riprendiAcceso);
      tieniAcceso(false);
    };
  }, [autoVoice]);

  const say = useCallback(
    async (righe, testo) => {
      if (!has("voice_alerts")) return askUpgrade("voice_alerts", "La lettura vocale delle comande è inclusa nel piano Standard");
      // Tollera sia la comanda sia un testo già pronto
      const lista = Array.isArray(righe) ? righe : [];
      const frase = testo || (typeof righe === "string" ? righe : "");
      // Voce del dispositivo: italiana e gratuita. Voce del server: solo se scelta in Impostazioni.
      if ((settings?.voice_engine || "device") === "device") {
        annuncia(lista.length ? lista : frase, dept, {
          completo: has("voice_full"),
          salvata: settings?.voice_device || "",
          asportoLabel: t("c.takeaway"),
          tavoloLabel: t("s.table"),
        });
        return;
      }
      try {
        const { url } = await p("/tts", { text: frase });
        audio.current?.pause();
        audio.current = new Audio(`${API_BASE}${url}`);
        await audio.current.play();
      } catch (e) {
        toast.error(errMsg(e));
      }
    },
    [has, askUpgrade, settings, dept, t]
  );

  useEffect(() => {
    if (!autoVoice || !rows.length) return;
    const fresh = rows.filter((r) => !spokenIds.current.has(r.order_id) && r.items.some((i) => !i.ready));
    if (!fresh.length) return;
    fresh.forEach((r) => spokenIds.current.add(r.order_id));
    // Il primo caricamento non si legge: altrimenti all'apertura recita tutto lo storico
    if (!primed.current) {
      primed.current = true;
      return;
    }
    say(fresh, fresh.map((r) => spoken(r, t, has("voice_full"))).join(" "));
  }, [rows, autoVoice, say, t, has]);

  // Richieste della sala: si segnano "viste" e, se la voce è accesa, si annunciano una volta sola
  useEffect(() => {
    const nuove = [];
    rows.forEach((r) =>
      r.items.forEach((i) => {
        if (!i.req || i.req.reply || seenReq.current.has(i.id + i.req.at)) return;
        seenReq.current.add(i.id + i.req.at);
        nuove.push({ r, i });
        p(`/kds/${r.order_id}/${i.id}/seen`).catch(() => {});
      })
    );
    if (!nuove.length || !autoVoice) return;
    const frase = nuove
      .map(({ r, i }) => {
        const dove = r.mode === "asporto" ? r.tables[0] : `${t("s.table")} ${r.tables.join(", ")}`;
        const cosa = i.req.action === "prima" ? t("q.first") : i.req.action === "attesa" ? t("q.wait")
          : i.req.action === "dopo" ? t("q.badgeAfter", { n: i.req.after_name }) : t("q.void");
        return `${dove}. ${i.name}: ${cosa}.`;
      })
      .join(" ");
    say([], frase);
  }, [rows, autoVoice, say, t]);

  const toggleFull = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
      setFull(false);
    } else {
      el.requestFullscreen?.();
      setFull(true);
    }
  };

  const startPress = (oid, iid) => {
    press.current = setTimeout(async () => {
      try {
        await p(`/kds/${oid}/${iid}/unready`);
        toast.success(t("m.undone"));
        load();
      } catch (e) {
        toast.error(errMsg(e));
      }
    }, 800);
  };
  const endPress = () => {
    clearTimeout(press.current);
    press.current = null;
  };

  const color = (m) => (m > 20 ? "border-rose-500 bg-rose-50" : m >= 12 ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50");
  const cardColor = (r) =>
    r.mode === "banco" ? "border-violet-500 bg-violet-50" : r.mode === "asporto" ? "border-sky-500 bg-sky-50" : color(r.minutes);

  const setDoughTotal = async () => {
    try {
      setDough(await p("/dough", { total: Number(doughVal) || 0, by: user?.name || dept }));
      setDoughVal("");
      toast.success(t("x.dough.ok"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const adjustDough = async (delta) => {
    setDough(await p("/dough", { delta, by: user?.name || dept }));
  };

  const toggleSuspend = async () => {
    try {
      const next = !susp?.suspended;
      setSusp(await p("/takeaway/suspend", { suspended: next, reason: suspReason, by: user?.name || dept }));
      setSuspOpen(false);
      setSuspReason("");
      toast.success(next ? t("x.susp.ok") : t("x.susp.resumed"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="fade-up space-y-5" data-testid="monitor-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("m.stations")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("m.monitor", { d: dept })}</h1>
          <p className="mt-1 text-xs text-slate-500" data-testid="done-today">
            {t("m.today", { dishes: done.dishes, tickets: done.tickets })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={autoVoice ? "default" : "outline"}
            data-testid="auto-voice-btn"
            onClick={async () => {
              if (!has("voice_alerts")) return askUpgrade("voice_alerts", "La lettura vocale delle comande è inclusa nel piano Standard");
              if (!autoVoice && !sbloccato) {
                // iOS e Chrome sbloccano voce e audio solo dopo un tocco vero
                await sbloccaVoce();
                setSbloccato(true);
              }
              setAutoVoice((v) => !v);
              toast.info(autoVoice ? t("m.voiceStop") : t("m.voiceStart"));
            }}
          >
            <Volume2 className="mr-1.5 h-4 w-4" /> {autoVoice ? t("m.voiceOn") : t("m.voiceOff")}
          </Button>
          <Button
            variant={susp?.suspended ? "default" : "outline"}
            data-testid="suspend-takeaway-btn"
            onClick={() => (susp?.suspended ? toggleSuspend() : setSuspOpen(true))}
          >
            {susp?.suspended ? <Play className="mr-1.5 h-4 w-4" /> : <Ban className="mr-1.5 h-4 w-4" />}
            {susp?.suspended ? t("x.susp.off") : t("x.susp.on")}
          </Button>
          <Button
            variant="outline"
            data-testid="fatte-btn"
            onClick={async () => {
              if (fatte) {
                fatteRef.current = false;
                return setFatte(null);
              }
              fatteRef.current = true;
              setFatte(await g(`/kds/${dept}/fatte`));
            }}
          >
            <ListRestart className="mr-1.5 h-4 w-4" /> {t("m.recent")}
          </Button>
          <Button
            variant={maniSporche ? "default" : "outline"}
            data-testid="mani-sporche-btn"
            onClick={() => {
              const next = !maniSporche;
              setManiSporche(next);
              localStorage.setItem("rh_mani_sporche", next ? "1" : "0");
            }}
          >
            <Hand className="mr-1.5 h-4 w-4" /> {maniSporche ? t("m.glovesOn") : t("m.gloves")}
          </Button>
          <Button variant="outline" onClick={toggleFull} data-testid="fullscreen-btn">
            {full ? <Minimize className="mr-1.5 h-4 w-4" /> : <Maximize className="mr-1.5 h-4 w-4" />} {t("m.fullscreen")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {departments.map((d) => (
          <button
            key={d}
            data-testid={`dept-tab-${d}`}
            onClick={() => setDept(d)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              d === dept ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      {maniSporche && (
        <p className="rounded-md border-2 border-slate-300 bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-700" data-testid="mani-sporche-hint">
          {t("m.glovesHint")}
        </p>
      )}

      {susp?.suspended && (
        <p className="rounded-md border-2 border-rose-400 bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-800" data-testid="takeaway-suspended-banner">
          {t("x.susp.suspended", { reason: susp.reason || "—" })}
        </p>
      )}

      {dept === "Pizzeria" && (
        <Card className="flex flex-wrap items-center gap-3 p-4" data-testid="dough-panel">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
            <Pizza className="h-3.5 w-3.5" /> {t("x.dough.title")}
          </span>
          {dough?.total ? (
            <>
              <span
                className={`font-num rounded-full px-3 py-1 text-sm font-bold ${
                  dough.level === "red" ? "bg-rose-200 text-rose-800" : dough.level === "amber" ? "bg-amber-200 text-amber-900" : "bg-emerald-100 text-emerald-700"
                }`}
                data-testid="dough-left"
              >
                {t("x.dough.left", { n: dough.left })}
              </span>
              <span className="font-num text-xs text-slate-500" data-testid="dough-used">{t("x.dough.used", { n: dough.used })}</span>
              <Button size="sm" variant="outline" onClick={() => adjustDough(-1)} data-testid="dough-minus-btn">−</Button>
              <Button size="sm" variant="outline" onClick={() => adjustDough(1)} data-testid="dough-plus-btn">+</Button>
            </>
          ) : (
            <span className="text-xs text-slate-500">{t("x.dough.off")}</span>
          )}
          <span className="ml-auto flex items-center gap-2">
            <Input className="h-9 w-24" type="number" min="0" placeholder={t("x.dough.today")} value={doughVal} onChange={(e) => setDoughVal(e.target.value)} data-testid="dough-input" />
            <Button size="sm" onClick={setDoughTotal} disabled={doughVal === ""} data-testid="dough-set-btn">
              {t("x.dough.set")}
            </Button>
          </span>
        </Card>
      )}

      {segnali.length > 0 && (
        <div className="space-y-1.5" data-testid="segnali-reparto">
          {segnali.slice(0, 3).map((s) => (
            <p key={s.id}
               className="flex items-center gap-2 rounded-lg border-2 border-sky-300 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-900"
               data-testid={`segnale-${s.id}`}>
              <MessageSquare className="h-4 w-4 shrink-0" />
              <span className="uppercase">{s.from_dept}</span>: {s.testo}
              {s.table && <span className="text-sky-700">· tavolo {s.table}</span>}
            </p>
          ))}
        </div>
      )}

      {rows.length > 1 && !maniSporche && (
        <div className="flex flex-wrap items-center gap-2" data-testid="ordine-barra">
          <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <GripVertical className="h-3.5 w-3.5" /> {t("m.dragHint")}
          </span>
          {manuale && (
            <>
              <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white" data-testid="ordine-manuale-badge">
                {t("m.manualOrder")}
              </span>
              <Button size="sm" variant="outline" onClick={azzera} data-testid="ordine-auto-btn">
                <ListRestart className="mr-1.5 h-3.5 w-3.5" /> {t("m.autoOrder")}
              </Button>
            </>
          )}
        </div>
      )}

      {fatte && (
        <Card className="border-2 border-emerald-300 bg-emerald-50/60 p-4" data-testid="fatte-panel">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800">{t("m.recent")}</p>
          {fatte.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">{t("m.recentEmpty")}</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {fatte.map((f) => (
                <li key={f.item_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-3 py-2 text-sm">
                  <span>
                    <b>{f.qty}× {f.name}</b>
                    <span className="text-slate-500"> · {f.group_name || (f.tables || []).join(" + ")} · {t("m.round")} {f.giro}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`richiama-${f.item_id}`}
                    onClick={async () => {
                      await p(`/kds/${f.order_id}/${f.item_id}/unready`);
                      setFatte(await g(`/kds/${dept}/fatte`));
                      load();
                      toast.info(t("m.recalled"));
                    }}
                  >
                    <Undo2 className="mr-1 h-3.5 w-3.5" /> {t("m.recall")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {rows.length === 0 && <p className="text-sm text-slate-500">{t("m.none", { d: dept })}</p>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {righe.map((r) => (
          <Card
            key={r.order_id}
            data-kds-card={r.order_id}
            className={`border-2 p-5 transition-transform ${cardColor(r)} ${
              nuovi.includes(r.order_id) ? "animate-pulse ring-4 ring-amber-400" : ""
            } ${
              dragId === r.order_id ? "scale-[1.02] shadow-2xl ring-2 ring-slate-900" : ""
            }`}
            data-testid={`kds-order-${r.order_id}`}
          >
            {nuovi.includes(r.order_id) && (
              <button
                data-testid={`nuova-${r.order_id}`}
                onClick={() => setNuovi((n) => n.filter((x) => x !== r.order_id))}
                className="mb-3 w-full rounded-md bg-amber-400 px-3 py-2 text-sm font-bold uppercase tracking-wide text-amber-950"
              >
                {t("m.newTicket")}
              </button>
            )}
            <div
              className="flex touch-none cursor-grab select-none items-start justify-between gap-2 active:cursor-grabbing"
              data-testid={`kds-drag-${r.order_id}`}
              {...(maniSporche ? {} : handlers(r.order_id))}
            >
              <div>
                <p className="font-head text-xl font-bold">
                  <GripVertical className="mr-1 inline h-4 w-4 text-slate-400" />
                  {r.group_name || (r.mode === "asporto" ? r.tables[0] : `${t("s.table")} ${r.tables.join(" + ")}`)}
                </p>
                {r.mode !== "sala" && (
                  <span
                    className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                      r.mode === "banco" ? "bg-violet-600 text-white" : "bg-sky-600 text-white"
                    }`}
                    data-testid={`kds-mode-${r.order_id}`}
                  >
                    {r.mode === "banco" ? "BANCO" : `ASPORTO · DA INCARTARE${r.pickup_time ? ` · ${r.pickup_time}` : ""}`}
                  </span>
                )}
                <p className="text-xs text-slate-600">
                  {r.waiter} · {r.covers} {t("s.covers")}
                  {r.channel && ` · ${r.channel}`}
                </p>
              </div>
              <span className="font-num rounded-md bg-white/70 px-2 py-1 text-sm font-bold" data-testid={`kds-minutes-${r.order_id}`}>
                {r.minutes}′
              </span>
            </div>

            {r.sync_wait > 0 && (
              <p className="mt-3 flex items-center gap-1.5 rounded-md bg-slate-900 px-2.5 py-2 text-xs font-semibold text-white" data-testid={`sync-hint-${r.order_id}`}>
                <Timer className="h-3.5 w-3.5 shrink-0" /> {t("m.waitHint", { n: r.sync_wait })}
              </p>
            )}

            {r.same_table.length > 0 && (
              <div className="mt-3 rounded-md border border-slate-200 bg-white/70 p-2.5" data-testid={`same-table-${r.order_id}`}>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{t("m.sameTable")}</p>
                <div className="mt-1 space-y-0.5">
                  {r.same_table.map((s) => (
                    <p key={s.department} className="flex items-center justify-between text-[11px]">
                      <span className="font-medium">{s.department} · {s.items} {t("m.dishes")}</span>
                      <span className={s.done ? "font-bold text-emerald-700" : s.eta < 0 ? "font-bold text-rose-600" : "text-slate-600"}>
                        {s.done ? t("m.readyBadge") : s.eta < 0 ? t("m.late", { n: -s.eta }) : t("m.in", { n: s.eta })}
                      </span>
                    </p>
                  ))}
                </div>
                {parlaPer === r.order_id ? (
                  <ParlaReparto dept={dept} riga={r} by={user?.name} onClose={() => setParlaPer(null)} />
                ) : (
                  <button
                    onClick={() => setParlaPer(r.order_id)}
                    data-testid={`parla-apri-${r.order_id}`}
                    className="mt-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-sky-800"
                  >
                    <MessageSquare className="h-3.5 w-3.5" /> Parla con gli altri reparti
                  </button>
                )}
              </div>
            )}

            <div className="mt-4 space-y-2">
              {maniSporche ? (
                <BottonePronto
                  testid={`ready-all-${r.order_id}`}
                  label={t("m.readyAll")}
                  hint={t("m.holdToConfirm")}
                  onDone={() => prontoTutta(r)}
                />
              ) : (
                <Button className="w-full whitespace-normal py-6 text-sm font-bold uppercase leading-tight tracking-wide sm:text-base"
                        data-testid={`ready-all-${r.order_id}`} onClick={() => prontoTutta(r)}>
                  <Check className="mr-2 h-5 w-5" /> {t("m.readyAll")}
                </Button>
              )}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="flex-1" data-testid={`chiama-${r.order_id}`}
                        onClick={async () => {
                          try {
                            await p(`/kds/${r.order_id}/${dept}/chiama-cameriere`);
                            toast.success(t("m.calledWaiter", { t: r.tables.join(", ") }));
                          } catch (e) {
                            toast.error(errMsg(e));
                          }
                        }}>
                  <BellRing className="mr-1.5 h-4 w-4" /> {t("m.callWaiter")}
                </Button>
                <Button variant="ghost" data-testid={`dettaglio-${r.order_id}`}
                        onClick={() => setDettaglio((d) => ({ ...d, [r.order_id]: !d[r.order_id] }))}>
                  {dettaglio[r.order_id] ? t("m.hideDetail") : t("m.showDetail")}
                </Button>
              </div>
            </div>

            <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500" data-testid={`now-title-${r.order_id}`}>
              {t("m.nowTitle")}
            </p>
            <div className="mt-1.5 space-y-2">
              {conAltriReparti(r).map((i, idx, arr) => (
                <div key={i.id}>
                {(idx === 0 || (arr[idx - 1].giro || 1) !== (i.giro || 1)) && (
                  <div className="mb-1.5 mt-3 flex items-center gap-2 first:mt-0" data-testid={`kds-giro-sep-${r.order_id}-${i.giro || 1}`}>
                    <span className="rounded bg-slate-900 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                      {t("c.giroLabel")} {i.giro || 1}
                    </span>
                    <span className="h-0.5 flex-1 bg-slate-900/70" />
                  </div>
                )}
                {i._altro ? (
                <div
                  className={`cursor-default select-none rounded-md border-2 border-dashed px-2 py-1.5 opacity-90 ${COL_REPARTO[i.department] || "border-slate-300 bg-slate-50 text-slate-700"}`}
                  data-testid={`kds-altro-${i.id}`}
                >
                  <p className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold leading-tight">
                    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white ${BADGE_REPARTO[i.department] || "bg-slate-500"}`}>
                      {i.department}
                    </span>
                    <span><span className="font-num">{i.qty}×</span> {i.name}</span>
                    {i.ready && <span className="text-[10px] font-bold uppercase">· {t("m.readyBadge")}</span>}
                  </p>
                  {i.note && <p className="text-[11px] italic leading-tight opacity-80">{i.note}</p>}
                </div>
                ) : (
                <div
                  className={`rounded-md p-2 ${i.req && !i.req.reply ? "animate-pulse border-2 border-rose-500 bg-rose-100" : "bg-white/70"}`}
                  data-testid={`kds-item-${i.id}`}
                >
                {i.req && !i.req.reply && (
                  <p className="mb-1.5 rounded bg-rose-600 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white" data-testid={`req-alert-${i.id}`}>
                    {t("q.title")}: {i.req.action === "prima" ? t("q.first") : i.req.action === "attesa" ? t("q.wait")
                      : i.req.action === "dopo" ? t("q.badgeAfter", { n: i.req.after_name }) : t("q.void")}
                  </p>
                )}
                <div className={`flex gap-2 ${maniSporche ? "flex-col items-stretch" : "items-start justify-between"}`}>
                  <div className="min-w-0">
                    {(i.priority || i.hold) && (
                      <p className="mb-0.5 flex gap-1" data-testid={`kds-flagsq-${i.id}`}>
                        {i.priority && <span className="rounded bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">{t("q.priority")}</span>}
                        {i.hold && <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">{t("q.hold")}</span>}
                      </p>
                    )}
                    <p className="text-sm font-semibold">
                      <span className="font-num">{i.qty}×</span> {i.name}
                    </p>
                    {(i.removed || []).length > 0 && (
                      <p className="text-[12px] font-bold uppercase leading-tight text-rose-600" data-testid={`kds-senza-${i.id}`}>
                        senza {i.removed.join(", ")}
                      </p>
                    )}
                    {(i.extras || []).length > 0 && (
                      <p className="text-base font-bold leading-tight text-emerald-700" data-testid={`kds-conq-${i.id}`}>
                        + {i.extras.map((e) => e.name).join(", ")}
                      </p>
                    )}
                    <p className="text-[11px] text-slate-500">
                      {t("c.giroLabel")} {i.giro || 1}
                      {i.surcharge_label && !(i.extras || []).length && ` · ${i.surcharge_label}`}
                      {i.grams > 0 && ` · ${i.grams} g`}
                      {i.cooking && ` · ${i.cooking}`}
                    </p>
                    {i.note && (
                      <p className="mt-1 rounded-md border-2 border-amber-400 bg-amber-50 px-2 py-1.5 text-lg font-bold leading-tight text-amber-900" data-testid={`kds-nota-${i.id}`}>
                        {i.note}
                      </p>
                    )}
                    {(i.takeaway || i.to_cut) && (
                      <p className="text-[10px] font-bold uppercase text-violet-700" data-testid={`kds-flags-${i.id}`}>
                        {[i.takeaway && t("x.opt.takeaway"), i.to_cut && t("x.opt.cut")].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {i.allergens.length > 0 && <p className="text-[11px] font-medium text-rose-600">{i.allergens.join(", ")}</p>}
                  </div>
                  {!dettaglio[r.order_id] ? null : i.ready ? (
                    <button
                      className="shrink-0 select-none rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-right text-[10px] font-bold uppercase leading-tight text-emerald-700 active:scale-95"
                      data-testid={`unready-${i.id}`}
                      title={t("m.holdToUndo")}
                      onPointerDown={() => startPress(r.order_id, i.id)}
                      onPointerUp={endPress}
                      onPointerLeave={endPress}
                    >
                      {t("m.readyBadge")}
                      {i.notified && <span className="block font-medium normal-case text-emerald-600" data-testid={`notified-${i.id}`}>{t("m.notified")}</span>}
                      <span className="mt-0.5 flex items-center justify-end gap-0.5 font-medium normal-case text-slate-400">
                        <Undo2 className="h-2.5 w-2.5" /> {t("m.holdToUndo")}
                      </span>
                    </button>
                  ) : maniSporche ? (
                    <BottonePronto
                      testid={`ready-${i.id}`}
                      disabled={i.hold}
                      label={t("m.readyBtn")}
                      hint={t("m.holdToConfirm")}
                      onDone={async () => {
                        await p(`/kds/${r.order_id}/${i.id}/ready`);
                        toast.success(t("m.waiterNotified"));
                        load();
                      }}
                    />
                  ) : (
                    <Button
                      size="sm"
                      disabled={i.hold}
                      data-testid={`ready-${i.id}`}
                      onClick={async () => {
                        await p(`/kds/${r.order_id}/${i.id}/ready`);
                        toast.success(t("m.waiterNotified"));
                        load();
                      }}
                    >
                      <Check className="mr-1 h-3.5 w-3.5" /> {t("m.readyBtn")}
                    </Button>
                  )}
                </div>
                {i.req && !i.req.reply && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`req-reply-${i.id}`}>
                    {(i.req.action === "annulla"
                      ? [["annullata", t("q.voidBtn")], ["in_cottura", t("q.cookingBtn")]]
                      : [["ok", t("q.okBtn")], ["in_cottura", t("q.cookingBtn")]]
                    ).map(([code, label]) => (
                      <Button
                        key={code}
                        size="sm"
                        variant={code === "in_cottura" ? "outline" : "default"}
                        className={code === "in_cottura" ? "bg-white" : ""}
                        data-testid={`reply-${code}-${i.id}`}
                        onClick={async () => {
                          try {
                            await p(`/kds/${r.order_id}/${i.id}/reply`, { code });
                            load();
                          } catch (e) {
                            toast.error(errMsg(e));
                          }
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                )}
                </div>
                )}
                </div>
              ))}
            </div>

            {r.upcoming?.length > 0 && (
              <div className="mt-4 rounded-md border border-dashed border-slate-300 bg-slate-100/70" data-testid={`soon-block-${r.order_id}`}>
                <button
                  className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left"
                  data-testid={`soon-toggle-${r.order_id}`}
                  onClick={() => setOpenSoon((s) => ({ ...s, [r.order_id]: !s[r.order_id] }))}
                >
                  <span>
                    <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{t("m.soonTitle")}</span>
                    <span className="font-num block text-[11px] text-slate-400">
                      {t("m.soonCount", { n: r.upcoming.reduce((s, i) => s + (i.qty || 1), 0) })}
                    </span>
                  </span>
                  {openSoon[r.order_id] ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />}
                </button>
                {openSoon[r.order_id] && (
                  <div className="max-h-52 space-y-1.5 overflow-y-auto px-2.5 pb-2.5">
                    <p className="text-[10px] italic text-slate-400">{t("m.soonHint")}</p>
                    {r.upcoming.map((i) => (
                      <div key={i.id} className="flex items-start justify-between gap-2 rounded bg-white/60 p-2" data-testid={`soon-item-${i.id}`}>
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-slate-500">
                            <span className="font-num">{i.qty}×</span> {i.name}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {t("c.giroLabel")} {i.giro || 1}
                            {i.grams > 0 && ` · ${i.grams} g`}
                            {i.cooking && ` · ${i.cooking}`}
                            {i.note && ` · ${i.note}`}
                          </p>
                          {(i.removed || []).length > 0 && (
                            <p className="text-[10px] font-bold uppercase text-rose-600" data-testid={`soon-senza-${i.id}`}>
                              senza {i.removed.join(", ")}
                            </p>
                          )}
                          {i.allergens?.length > 0 && <p className="text-[10px] text-rose-400">{i.allergens.join(", ")}</p>}
                          {i.prestarted && (
                            <p className="text-[10px] font-bold uppercase text-sky-600" data-testid={`prestarted-${i.id}`}>
                              {t("m.prestarted")}
                            </p>
                          )}
                        </div>
                        <button
                          className={`shrink-0 rounded border px-2 py-1 text-[10px] font-semibold active:scale-95 ${
                            i.prestarted ? "border-sky-300 bg-sky-50 text-sky-700" : "border-slate-300 bg-white text-slate-500"
                          }`}
                          data-testid={`prestart-${i.id}`}
                          onClick={async () => {
                            try {
                              await p(`/kds/${r.order_id}/${i.id}/prestart`);
                              load();
                            } catch (e) {
                              toast.error(errMsg(e));
                            }
                          }}
                        >
                          {i.prestarted ? t("m.prestarted") : t("m.prestart")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4">
              <Button variant="outline" className="w-full bg-white" data-testid={`repeat-${r.order_id}`} onClick={() => say([r], spoken(r, t, has("voice_full")))}>
                <Repeat className="mr-1.5 h-4 w-4" /> {t("m.repeat")}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={suspOpen} onOpenChange={setSuspOpen}>
        <DialogContent data-testid="suspend-dialog">
          <DialogHeader>
            <DialogTitle>{t("x.susp.on")}</DialogTitle>
          </DialogHeader>
          <Input placeholder={t("x.susp.reason")} value={suspReason} onChange={(e) => setSuspReason(e.target.value)} data-testid="suspend-reason-input" />
          <div className="flex flex-wrap gap-2">
            {["forno pieno", "troppe comande", "impasto finito"].map((s) => (
              <Button key={s} size="sm" variant="outline" onClick={() => setSuspReason(s)}>
                {s}
              </Button>
            ))}
          </div>
          <Button className="h-11 w-full" onClick={toggleSuspend} data-testid="confirm-suspend-btn">
            {t("x.susp.confirm")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
